// Shared logic for warfarin/INR sliding-scale administration checks, used by PRNAdminModal
// and SlidingScaleAdminModal.
//
// IMPORTANT: the "safe range" a reading is checked against is entirely defined by the
// prescriber via the sliding scale bands set up on the chart (Min/Max) - there is no
// separate hardcoded clinical threshold here. The prescriber is responsible for setting
// bands appropriate to the patient.

// The canonical { type, unit } pairs live in the reading registry (services/readings.js, mirroring
// kraken's App\Support\ReadingTypes) - these strings are what the backend filters on, so they must
// not be assembled locally.
import { BGL_READING, INR_READING } from '@/services/readings';

export function toNumberOrNull(value) {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
}

// A chart is treated as warfarin/INR-monitored if the drug name mentions warfarin, or the
// sliding scale itself is configured with the 'INR' scale type/name.
export function isWarfarinOrInrChart(chart) {
    if (!chart) return false;
    const nameMatch = chart.alias?.name?.toLowerCase().includes('warfarin');
    const slidingScaleMatch = chart.sliding_scale && chart.sliding_scale[0]?.name === 'INR';
    return !!(nameMatch || slidingScaleMatch);
}

// A sliding scale's `name` IS its reading type - it's set from the prescriber's scale-type
// dropdown (`scale.name = slidingScaleType.unit` in PrescriptionChart's addSlidingTable /
// updateSlidingScaleType), it's never free text, and PrescriptionChart already reconstructs that
// dropdown from `name` alone when re-charting. So it is the source of truth here too.
const READING_IDENTITY_BY_SCALE_NAME = {
    BGL: BGL_READING,
    INR: INR_READING,
};

// The pair to persist a reading taken against `chart` with.
//
// NOT derived from the scale's own reading_type/reading_unit - those were written client-side at
// prescribing time but kraken never persisted or returned them, so on read-back they were always
// undefined and the fallback silently applied (an INR stored as a BGL). They are deprecated; `name`
// carries the same information.
//
// Falls back to isWarfarinOrInrChart for a scale whose name isn't in the map, so the stored type
// can't disagree with the INR banner and safe-range check, which use that same predicate.
export function readingIdentityForChart(chart) {
    const byScaleName = READING_IDENTITY_BY_SCALE_NAME[chart?.sliding_scale?.[0]?.name];
    if (byScaleName) return { ...byScaleName };
    return isWarfarinOrInrChart(chart) ? { ...INR_READING } : { ...BGL_READING };
}

// Derives the overall safe range from the prescriber's sliding scale bands: the lowest
// configured Min and the highest configured Max across all bands.
// INR scales are restricted to a single band at prescribing time (PrescriptionChart.vue's
// addSlidingTable/updateSlidingScaleType/slidingScaleValidationError all enforce this - INR
// only needs one reading range in practice), so for INR this reduces to that one band's own
// Min/Max. Other scale types (e.g. BGL/insulin) can still have multiple bands, and continue
// to be collapsed into one overall envelope here rather than matched per-band.
export function getSafeRangeFromSlidingScale(slidingScale) {
    if (!slidingScale || !slidingScale.length) return { min: null, max: null };
    const mins = slidingScale.map(band => toNumberOrNull(band.min)).filter(v => v !== null);
    const maxs = slidingScale.map(band => toNumberOrNull(band.max)).filter(v => v !== null);
    return {
        min: mins.length ? Math.min(...mins) : null,
        max: maxs.length ? Math.max(...maxs) : null,
    };
}

// Selects the sliding scale band a reading falls into and returns its index.
//
// EVERY operand has to be coerced before comparing. `min`/`max` reach the frontend as strings
// (kraken's SlidingScaleResource casts them with `(string)`, and `<input type="number" v-model>`
// on the prescribing side yields strings too), so a bare `reading >= band.min` is a
// *lexicographic* comparison whenever the reading is also a string - `"9" >= "20"` is true,
// which selected the open-ended top band (and its much larger dose) for a reading of 9.
// The modals no longer stringify readings, but the min/max coercion here is still required
// until the string casts are removed from the backend chain.
//
// A band with no `max` is open-ended upwards. Where bands overlap the last match wins, which
// preserves the behaviour of the per-modal loops this replaces. Returns null when the reading
// isn't numeric or no band matches, so callers can leave the current selection untouched
// instead of silently falling back to the first band.
export function selectSlidingScaleBandIndex(slidingScale, reading) {
    if (!Array.isArray(slidingScale) || !slidingScale.length) return null;
    const value = toNumberOrNull(reading);
    if (value === null) return null;

    let matchedIndex = null;
    slidingScale.forEach((band, index) => {
        const min = toNumberOrNull(band.min);
        if (min === null || value < min) return;
        const max = toNumberOrNull(band.max);
        if (max === null || value <= max) matchedIndex = index;
    });
    return matchedIndex;
}

// Whether a reading falls outside the given { min, max } safe range. A null bound means
// that side of the range is unbounded (not configured).
export function isReadingOutsideRange(reading, range) {
    if (reading === '' || reading === null || reading === undefined) return false;
    const value = parseFloat(reading);
    if (isNaN(value)) return false;
    if (range.min !== null && value < range.min) return true;
    if (range.max !== null && value > range.max) return true;
    return false;
}
